import { Alert, App, Button, Checkbox, DatePicker, Grid, Modal, Select, Skeleton, Spin, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { TbArrowLeft, TbFolderShare, TbLink, TbX } from 'react-icons/tb'

import { formatDateUk } from '@/Ship/Helpers/fieldViews'
import { classNames } from '@/Ship/Helpers/classNames'

import { DISK_ROLE_LABEL, DISK_SHARE_ROLES } from '../Contracts/DiskShare'
import { personName, storageTitle } from '../Helpers/diskFormat'
import { diskNodeAbsoluteUrl } from '../Helpers/diskRoutes'
import { diskShareStore as store } from '../Stores/DiskShareStore'
import { DiskPrincipalAvatar, PRINCIPAL_KIND } from './DiskPrincipal'

import type { DiskRole } from '../Contracts/DiskNode'
import type {
    DiskDirectShare,
    DiskInheritedShare,
    DiskPrincipalOption,
    DiskPrincipalRow,
    DiskPrincipalType,
} from '../Contracts/DiskShare'
import type { DiskExpiryChoice, DiskInheritedFor } from '../Stores/DiskShareStore'
import type { Dayjs } from 'dayjs'
import type { ReactNode } from 'react'

import styles from './DiskShare.module.css'

/** Що дає роль — правда з `DiskNodeRule` (`DISK-001` §3.2). */
const roleHint = (role: DiskRole, editorsCanShare: boolean): string => {
    if (role === 'viewer') return 'Відкриває й скачує'
    if (role === 'editor') {
        return editorsCanShare
            ? 'Також завантажує, перейменовує, переміщує, кладе в кошик і ділиться'
            : 'Також завантажує, перейменовує, переміщує й кладе в кошик'
    }
    if (role === 'manager') return 'Також керує доступом і видаляє з кошика назавжди'

    return 'Відкриває й скачує'
}

const GROUPS: { type: DiskPrincipalType; label: string }[] = [
    { type: 'user', label: 'Співробітники' },
    { type: 'department', label: 'Відділи — разом із підвідділами' },
    { type: 'role', label: 'Ролі' },
    { type: 'company', label: 'Уся компанія' },
]

const EXPIRY_OPTIONS: { value: DiskExpiryChoice; label: string }[] = [
    { value: 'none', label: 'Без строку' },
    { value: 'week', label: '7 днів' },
    { value: 'month', label: '30 днів' },
    { value: 'date', label: 'До дати…' },
]

/** Сьогодні ще можна: строк — до кінця дня. */
const beforeToday = (date: Dayjs): boolean => date.isBefore(dayjs(), 'day')

const grantedTip = (row: { grantedBy: DiskPrincipalRow['principalUser']; grantedAt: string | null }): string =>
    row.grantedBy || row.grantedAt
        ? `Надав${row.grantedBy ? ` ${personName(row.grantedBy)}` : 'о'}${row.grantedAt ? `, ${formatDateUk(row.grantedAt)}` : ''}`
        : ''

/** Варіанти ролі для селекта: вимкнені — з причиною замість опису. */
const roleOptions = (floor: DiskInheritedFor | null, current?: DiskRole) => {
    const editorsCanShare = store.data?.storage.editorsCanShare === true
    const roles = current !== undefined && !DISK_SHARE_ROLES.includes(current) ? [current, ...DISK_SHARE_ROLES] : DISK_SHARE_ROLES

    return roles.map((role) => {
        const block = role === current ? null : store.roleBlock(role, floor)

        return {
            value: role,
            disabled: block !== null,
            label: DISK_ROLE_LABEL[role],
            hint: block ?? roleHint(role, editorsCanShare),
        }
    })
}

const renderRoleOption = (option: { data: { label?: ReactNode; hint?: string } }) => (
    <span className={classNames(styles['roleOption'])}>
        <span>{option.data.label}</span>
        {option.data.hint && <span className={classNames(styles['roleOptionHint'])}>{option.data.hint}</span>}
    </span>
)

const Who = ({ row, sub }: { row: DiskPrincipalRow; sub?: ReactNode }) => (
    <>
        <DiskPrincipalAvatar type={row.principalType} user={row.principalUser} title={row.principalTitle} />
        <span className={classNames(styles['who'])}>
            <span className={classNames(styles['whoTitle'])} title={row.principalTitle}>
                {row.principalTitle}
            </span>
            <span className={classNames(styles['whoSub'])}>{sub ?? PRINCIPAL_KIND[row.principalType]}</span>
        </span>
    </>
)

const ExpiryTag = ({ expiresAt }: { expiresAt: string | null }) =>
    expiresAt ? <Tag>до {formatDateUk(expiresAt)}</Tag> : null

/** Блок «Додати адресатів»: пошук, роль, строк, «Повідомити», «Надати доступ». */
const AddRecipients = observer(() => {
    const { message } = App.useApp()
    const floor = store.pickedFloor
    const known = new Map<string, DiskPrincipalOption>()

    for (const option of [...store.picked, ...store.availableOptions]) known.set(option.principalKey, option)

    const groups = GROUPS.map((group) => ({
        label: group.label,
        title: group.label,
        options: [...known.values()]
            .filter((option) => option.principalType === group.type)
            .map((option) => ({ value: option.principalKey, label: option.title })),
    })).filter((group) => group.options.length > 0)

    const grant = async (): Promise<void> => {
        const result = await store.grant()

        if (result.ok) void message.success(result.count === 1 ? 'Доступ надано' : `Доступ надано: ${result.count}`)
        else void message.error(result.error)
    }

    return (
        <div className={classNames(styles['section'])} data-testid="disk-share-add">
            <h4 className={classNames(styles['sectionTitle'])}>Додати адресатів</h4>
            <Select<string[]>
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Співробітники, відділи, ролі"
                aria-label="Кому надати доступ"
                showSearch={{ filterOption: false, searchValue: store.query, onSearch: (value) => store.setQuery(value) }}
                value={store.picked.map((option) => option.principalKey)}
                onChange={(keys) => {
                    store.setPicked(keys)
                    store.setQuery('')
                }}
                options={groups}
                optionRender={(option) => {
                    const item = known.get(String(option.value))
                    if (!item) return option.label

                    return (
                        <span className={classNames(styles['option'])}>
                            <DiskPrincipalAvatar type={item.principalType} user={item.user} title={item.title} size={28} />
                            <span className={classNames(styles['optionText'])}>
                                <span className={classNames(styles['whoTitle'])}>{item.title}</span>
                                {item.subtitle && <span className={classNames(styles['whoSub'])}>{item.subtitle}</span>}
                            </span>
                        </span>
                    )
                }}
                notFoundContent={store.isSearching ? <Spin size="small" /> : 'Нікого не знайдено'}
                data-testid="disk-share-recipients"
            />
            <div className={classNames(styles['addRow'])}>
                <Select<DiskRole>
                    value={store.role}
                    onChange={(role) => store.setRole(role)}
                    options={roleOptions(floor)}
                    optionRender={renderRoleOption}
                    popupMatchSelectWidth={300}
                    style={{ width: 150 }}
                    aria-label="Роль"
                    data-testid="disk-share-role"
                />
                <Select<DiskExpiryChoice>
                    value={store.expiry}
                    onChange={(value) => store.setExpiry(value)}
                    options={EXPIRY_OPTIONS}
                    style={{ width: 140 }}
                    aria-label="Строк доступу"
                />
                {store.expiry === 'date' && (
                    <DatePicker
                        value={store.expiryDate ? dayjs(store.expiryDate) : null}
                        onChange={(date: Dayjs | null) => store.setExpiryDate(date ? date.format('YYYY-MM-DD') : null)}
                        format="DD.MM.YYYY"
                        disabledDate={beforeToday}
                        placeholder="Дата"
                        aria-label="Діє до"
                    />
                )}
                <Checkbox
                    checked={store.notify && !store.pickedOnlyCompany}
                    disabled={store.pickedOnlyCompany}
                    onChange={(event) => store.setNotify(event.target.checked)}
                >
                    Повідомити
                </Checkbox>
                <Button
                    type="primary"
                    disabled={!store.canGrant}
                    loading={store.isGranting}
                    onClick={() => void grant()}
                    data-testid="disk-share-grant"
                >
                    Надати доступ
                </Button>
            </div>
            {floor !== null && (
                <div className={classNames(styles['addNote'])}>
                    Вибраний адресат уже має роль «{DISK_ROLE_LABEL[floor.role]}» від {floor.source}: нижчу видати не
                    можна.
                </div>
            )}
            {store.pickedCompany && (
                <div className={classNames(styles['addNote'])}>
                    «Усій компанії» сповіщення не надсилаються: елемент з’явиться в «Спільних зі мною» кожного
                    співробітника.
                </div>
            )}
        </div>
    )
})

/** «Надано тут»: роль і строк змінюються одразу, «×» — прибрати з «Скасувати». */
const DirectRow = observer(({ row }: { row: DiskDirectShare }) => {
    const { message } = App.useApp()
    const editable = store.canTouch(row)
    const busy = store.isBusy(row.uuid)
    const floor = row.inheritedRole
        ? { role: row.inheritedRole.role, source: 'папки вище чи простору' }
        : null
    const tip = grantedTip(row)
    const report = (result: { ok: true } | { ok: false; error: string }, done: string): void => {
        if (result.ok) void message.success(done)
        else void message.error(result.error)
    }

    const remove = async (): Promise<void> => {
        const nodeUuid = store.node?.uuid
        const result = await store.remove(row)

        if (!result.ok || !nodeUuid) {
            if (!result.ok) void message.error(result.error)

            return
        }

        const key = `disk-unshare-${row.uuid}`
        void message.open({
            key,
            type: 'success',
            duration: 8,
            content: (
                <span>
                    Доступ «{row.principalTitle}» прибрано{' '}
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            message.destroy(key)
                            void store.undoRemove(nodeUuid, result.removed).then((undo) =>
                                report(undo, 'Доступ повернуто')
                            )
                        }}
                        data-testid="disk-share-undo"
                    >
                        Скасувати
                    </Button>
                </span>
            ),
        })
    }

    const sub = (
        <>
            {PRINCIPAL_KIND[row.principalType]}
            {tip ? ` · ${tip}` : ''}
        </>
    )

    return (
        <div className={classNames(styles['row'])} data-testid="disk-share-direct-row">
            <Who row={row} sub={sub} />
            <span className={classNames(styles['controls'])}>
                {row.effectiveRole && (
                    <Tooltip title="Через відділ, роль чи учасника простору людина має вищу роль, ніж у цьому рядку">
                        <Tag color="blue">Фактично: {row.effectiveRole.roleLabel}</Tag>
                    </Tooltip>
                )}
                {editable ? (
                    <>
                        <Select<DiskRole>
                            size="small"
                            value={row.role}
                            disabled={busy}
                            onChange={(role) => void store.changeRole(row, role).then((result) => report(result, 'Роль змінено'))}
                            options={roleOptions(floor, row.role)}
                            optionRender={renderRoleOption}
                            popupMatchSelectWidth={300}
                            style={{ width: 132 }}
                            aria-label={`Роль: ${row.principalTitle}`}
                            data-testid="disk-share-row-role"
                        />
                        <DatePicker
                            size="small"
                            value={row.expiresAt ? dayjs(row.expiresAt) : null}
                            disabled={busy}
                            onChange={(date: Dayjs | null) =>
                                void store
                                    .changeExpiry(row, date ? date.format('YYYY-MM-DD') : null)
                                    .then((result) => report(result, date ? 'Строк змінено' : 'Строк прибрано'))
                            }
                            format="DD.MM.YYYY"
                            disabledDate={beforeToday}
                            placeholder="Без строку"
                            style={{ width: 128 }}
                            aria-label={`Строк: ${row.principalTitle}`}
                        />
                        <Tooltip title="Прибрати доступ">
                            <Button
                                type="text"
                                size="small"
                                icon={<TbX />}
                                loading={busy}
                                onClick={() => void remove()}
                                aria-label={`Прибрати доступ: ${row.principalTitle}`}
                                data-testid="disk-share-remove"
                            />
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <span className={classNames(styles['roleText'])}>{row.roleLabel}</span>
                        <ExpiryTag expiresAt={row.expiresAt} />
                    </>
                )}
            </span>
        </div>
    )
})

/** Успадковані — сірим, без «×»: прибирають на папці, де надано (D-8). */
const InheritedGroup = observer(({ rows }: { rows: DiskInheritedShare[] }) => {
    const from = rows[0]?.fromNode ?? null

    return (
        <div className={classNames(styles['group'])} data-testid="disk-share-inherited">
            <div className={classNames(styles['groupHead'])}>
                <span>{from ? `Успадковано від «${from.name}»` : 'Успадковано від папки вище'}</span>
                {from && (
                    <Button
                        type="link"
                        size="small"
                        icon={<TbFolderShare />}
                        onClick={() => store.openAncestor(from)}
                        data-testid="disk-share-open-ancestor"
                    >
                        Доступ «{from.name}»
                    </Button>
                )}
            </div>
            <p className={classNames(styles['groupHint'])}>
                Діє на цю папку чи файл і все всередині; змінити чи прибрати — лише там, де надано.
            </p>
            {rows.map((row) => (
                <div key={row.uuid} className={classNames(styles['row'], styles['rowInherited'])}>
                    <Who row={row} sub={`${PRINCIPAL_KIND[row.principalType]}${grantedTip(row) ? ` · ${grantedTip(row)}` : ''}`} />
                    <span className={classNames(styles['controls'])}>
                        <span className={classNames(styles['roleText'])}>{row.roleLabel}</span>
                        <ExpiryTag expiresAt={row.expiresAt} />
                    </span>
                </div>
            ))}
        </div>
    )
})

/** «Хто має доступ»: моя роль, учасники простору, успадковані, надані тут. */
const AccessList = observer(() => {
    const data = store.data
    if (data === null) return null

    const inheritedByNode = new Map<string, DiskInheritedShare[]>()
    for (const row of data.inherited) {
        const key = row.fromNode?.uuid ?? ''
        inheritedByNode.set(key, [...(inheritedByNode.get(key) ?? []), row])
    }

    const me = data.me
    const personal = data.storage.type === 'personal'
    const spaceTitle = storageTitle(data.storage)

    return (
        <div className={classNames(styles['section'])} data-testid="disk-share-list">
            <h4 className={classNames(styles['sectionTitle'])}>Хто має доступ</h4>
            {me.role !== null && (
                <div className={classNames(styles['me'])} data-testid="disk-share-me">
                    Ваш доступ: <strong>{me.roleLabel}</strong>
                    {me.source && <span>— {me.source.title}</span>}
                    {me.source?.type === 'share' && me.source.node && me.source.node.uuid !== data.node.uuid && (
                        <Button type="link" size="small" onClick={() => me.source?.node && store.openAncestor(me.source.node)}>
                            від «{me.source.node.name}»
                        </Button>
                    )}
                </div>
            )}

            <div className={classNames(styles['group'])} data-testid="disk-share-members">
                <div className={classNames(styles['groupHead'])}>
                    <span>{personal ? 'Власник диска' : `Учасники простору «${spaceTitle}»`}</span>
                </div>
                <p className={classNames(styles['groupHint'])}>
                    {personal
                        ? 'Бачить і змінює все на своєму диску.'
                        : 'Мають доступ до всього простору. Тут склад лише для перегляду.'}
                </p>
                {personal && data.owner && (
                    <div className={classNames(styles['row'])}>
                        <DiskPrincipalAvatar type="user" user={data.owner} title={personName(data.owner)} />
                        <span className={classNames(styles['who'])}>
                            <span className={classNames(styles['whoTitle'])}>{personName(data.owner)}</span>
                            <span className={classNames(styles['whoSub'])}>Власник «Мого диска»</span>
                        </span>
                        <span className={classNames(styles['controls'])}>
                            <span className={classNames(styles['roleText'])}>{DISK_ROLE_LABEL.manager}</span>
                        </span>
                    </div>
                )}
                {data.storageMembers.map((row) => (
                    <div key={row.uuid} className={classNames(styles['row'])}>
                        <Who row={row} />
                        <span className={classNames(styles['controls'])}>
                            <span className={classNames(styles['roleText'])}>{row.roleLabel}</span>
                        </span>
                    </div>
                ))}
                {!personal && data.storageMembers.length === 0 && (
                    <div className={classNames(styles['empty'])}>Учасників немає — простір бачать лише адміністратори</div>
                )}
            </div>

            {[...inheritedByNode.entries()].map(([key, rows]) => (
                <InheritedGroup key={key || 'above'} rows={rows} />
            ))}

            <div className={classNames(styles['group'])} data-testid="disk-share-direct">
                <div className={classNames(styles['groupHead'])}>
                    <span>Надано тут</span>
                </div>
                {data.direct.length === 0 ? (
                    <div className={classNames(styles['empty'])}>
                        {data.node.root ? 'Доступ до всього простору — це його учасники' : 'Тут доступ ще не надавали'}
                    </div>
                ) : (
                    data.direct.map((row) => <DirectRow key={row.uuid} row={row} />)
                )}
            </div>
        </div>
    )
})

/**
 * Модалка «Поділитися» (`DISK-005` §3.6.2): хто має доступ до вузла й
 * надання доступу. Відкривають меню рядка, панель вибору (один елемент),
 * дровер «Властивості» й `Ctrl/Cmd+Shift+S`. Без права ділитися — лише
 * перелік. Публічних посилань тут поки немає (окремий наряд).
 */
export const DiskShareModal = observer(() => {
    const { message } = App.useApp()
    const screens = Grid.useBreakpoint()
    const compact = screens.sm === false
    const node = store.node
    const data = store.data
    const previous = store.trail[store.trail.length - 1]

    const copyLink = async (): Promise<void> => {
        if (!node) return

        try {
            await navigator.clipboard.writeText(diskNodeAbsoluteUrl(node.uuid))
            void message.success('Посилання скопійовано')
        } catch {
            void message.error('Не вдалося скопіювати: браузер не дав доступу до буфера обміну')
        }
    }

    const readOnlyNote =
        data === null || store.canShare
            ? null
            : data.node.root
              ? 'Доступ до всього простору задають його учасники. Поділитися можна папкою чи файлом усередині.'
              : `Змінювати доступ можуть менеджери простору${data.storage.editorsCanShare ? ' й редактори' : ''}.`

    return (
        <Modal
            open={node !== null}
            title={node ? `Доступ до «${node.name}»` : ''}
            onCancel={() => store.close()}
            footer={null}
            width={compact ? '100%' : 640}
            {...(compact ? { style: { top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 } } : {})}
            destroyOnHidden
            data-testid="disk-share-modal"
        >
            {previous && (
                <Button
                    type="link"
                    size="small"
                    icon={<TbArrowLeft />}
                    onClick={() => store.back()}
                    style={{ paddingLeft: 0, marginBottom: 8 }}
                >
                    Назад до «{previous.name}»
                </Button>
            )}
            {store.error !== null && (
                <Alert
                    type="error"
                    showIcon
                    title={store.error}
                    action={
                        <Button size="small" onClick={() => void store.load()}>
                            Повторити
                        </Button>
                    }
                />
            )}
            {data === null && store.isLoading && <Skeleton active />}
            {data !== null && (
                <Spin spinning={store.isLoading} delay={300}>
                    {store.canShare && <AddRecipients />}
                    {readOnlyNote && <div className={classNames(styles['addNote'])}>{readOnlyNote}</div>}
                    <AccessList />
                    <div className={classNames(styles['section'])}>
                        <h4 className={classNames(styles['sectionTitle'])}>Посилання</h4>
                        <div className={classNames(styles['link'])}>
                            <Button icon={<TbLink />} onClick={() => void copyLink()} data-testid="disk-share-copy-link">
                                Копіювати посилання
                            </Button>
                            <span className={classNames(styles['whoSub'])}>
                                Відкриють лише ті, хто має доступ до цього елемента
                            </span>
                        </div>
                    </div>
                </Spin>
            )}
        </Modal>
    )
})
